import React, { useState, useEffect } from "react";
import Meta from "./../components/Meta";
import Button from "./../components/Button";
import FormAlert from "./../components/FormAlert";
import ReservationForm from "./../components/ReservationForm";
import { useAuth } from "./../util/auth";
import { useRouter } from "./../util/router";
import { getUserReservations, updateReservation, cancelReservation, getAvailableTables } from "./../util/db";
import { Link } from "react-router-dom";

function MyReservationsPage() {
  const auth = useAuth();
  const router = useRouter();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formAlert, setFormAlert] = useState(null);
  const [editingReservation, setEditingReservation] = useState(null);

  useEffect(() => {
    // Only redirect if auth has finished initializing and user is not authenticated
    // Don't redirect if auth is still loading (auth.user === null)
    if (auth.user === false) {
      router.push("/auth/signin?next=/my-reservations&message=Please sign in to view your reservations");
      return;
    }

    if (auth.user) {
      loadReservations();
    }
  }, [auth.user, router]);

  const loadReservations = async () => {
    try {
      const userReservations = await getUserReservations(auth.user.email);
      
      // Get date from 7 days ago in YYYY-MM-DD format
      const date = new Date();
      date.setDate(date.getDate() - 7);
      const cutoffDate = date.toISOString().slice(0, 10);  // Gets YYYY-MM-DD
      
      const filteredAndSortedReservations = userReservations
        .filter(reservation => reservation.reservationDate >= cutoffDate)
        .sort((a, b) => {
          // Sort by date first
          const dateCompare = b.reservationDate.localeCompare(a.reservationDate);
          if (dateCompare !== 0) return dateCompare;
          // Then by time if dates are equal
          return b.startTime - a.startTime;
        });

      setReservations(filteredAndSortedReservations);
    } catch (error) {
      console.error('Error loading reservations:', error);
      setFormAlert({
        type: "error",
        message: "Failed to load your reservations. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (reservation) => {
    setEditingReservation(reservation);
  };

  const handleCancel = async (reservationId) => {
    if (!window.confirm("Are you sure you want to cancel this reservation?")) {
      return;
    }

    try {
      await cancelReservation(reservationId);
      setFormAlert({
        type: "success",
        message: "Reservation cancelled successfully."
      });
      loadReservations();
    } catch (error) {
      setFormAlert({
        type: "error",
        message: "Failed to cancel reservation. Please try again."
      });
    }
  };

  const handleUpdateReservation = async (data) => {
    setFormAlert({ type: "info", message: "Updating reservation..." });

    try {
      // Convert selected time to military format (e.g., "19:00" to 1900)
      const [hours, minutes] = data.time.split(":");
      const startTime = parseInt(hours + minutes);
      
      // Calculate end time properly handling minutes (90-minute reservation)
      const totalMinutes = (hours * 60 + parseInt(minutes)) + 90;
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      const endTime = (endHours * 100) + endMinutes;

      // Create update object with only the fields that have changed
      const updateData = {};
      
      // Only include fields that have changed, with validation
      const parsedPartySize = parseInt(data.partySize);
      const newPartySize = isNaN(parsedPartySize) ? editingReservation.partySize : parsedPartySize;
      const partySizeChanged = newPartySize !== editingReservation.partySize;
      if (partySizeChanged) {
        updateData.partySize = newPartySize;
      }
      const timeChanged = startTime !== editingReservation.startTime;
      if (timeChanged) {
        updateData.startTime = startTime;
        updateData.endTime = endTime;
      }
      const dateChanged = data.date !== editingReservation.reservationDate;
      if (dateChanged) {
        updateData.reservationDate = data.date;
      }
      if (data.name !== editingReservation.name) {
        updateData.name = data.name;
      }
      if (data.phoneNumber !== editingReservation.phoneNumber) {
        updateData.phoneNumber = data.phoneNumber;
      }
      if (data.reminderEmail !== editingReservation.reminderEmail) {
        updateData.reminderEmail = data.reminderEmail;
      }
      
      // Customer cannot change seat type - preserve the original seat type
      updateData.seatType = editingReservation.seatType || 'table';
      
      // Only preserve the original tableIds if party size, time, and date haven't changed
      // If any of these have changed, let the backend automatically reassign tables
      const needsTableReassignment = partySizeChanged || timeChanged || dateChanged;
      
      if (editingReservation.tableIds && !needsTableReassignment) {
        updateData.tableIds = editingReservation.tableIds;
      }

      await updateReservation(editingReservation.id, updateData);
      
      // Update the local reservations state to reflect the changes
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === editingReservation.id 
            ? { ...res, ...updateData } 
            : res
        )
      );

      setFormAlert({ type: "success", message: "Reservation updated successfully!" });
      setEditingReservation(null);
    } catch (error) {
      setFormAlert({ 
        type: "error", 
        message: error.message || "Failed to update reservation. Please try again." 
      });
    }
  };

  // Format time from military format (e.g., 1900) to 12-hour format (e.g., 7:00 PM)
  const formatTime = (militaryTime) => {
    const hours = Math.floor(militaryTime / 100);
    const minutes = militaryTime % 100;
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Format date to a readable string without timezone conversion
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Show loading indicator if auth is initializing
  if (auth.user === null || loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <Meta title="My Reservations - Sushi Edomata" />
      <section className="py-8 sm:py-12 bg-blue-50">
        <div className="container mx-auto px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-center mb-4 sm:mb-6">My Reservations</h1>
          
          {formAlert && (
            <div className="max-w-2xl mx-auto mb-4 sm:mb-6">
              <FormAlert type={formAlert.type} message={formAlert.message} />
            </div>
          )}

          {reservations.length === 0 ? (
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-4 sm:p-6 text-center">
              <p className="text-gray-600 mb-4">You don't have any reservations yet.</p>
              <Button
                onClick={() => router.push("/")}
                size="lg"
                className="w-full sm:w-auto"
              >
                Make a Reservation
              </Button>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto space-y-4">
              {reservations.map((reservation) => (
                <div key={reservation.id} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                  {editingReservation?.id === reservation.id ? (
                    <ReservationForm
                      onSubmit={handleUpdateReservation}
                      initialValues={{
                        date: reservation.reservationDate,
                        partySize: reservation.partySize,
                        time: `${Math.floor(reservation.startTime / 100).toString().padStart(2, '0')}:${(reservation.startTime % 100).toString().padStart(2, '0')}`,
                        name: reservation.name,
                        phoneNumber: reservation.phoneNumber,
                        seatType: reservation.seatType || 'table',
                        reminderEmail: reservation.reminderEmail
                      }}
                      submitButtonText="Save Changes"
                      excludeReservationId={reservation.id}
                      onCancel={() => setEditingReservation(null)}
                      showCancelButton={true}
                      cancelButtonText="Cancel"
                      disableSeatTypeSelection={true}
                    />
                  ) : (
                    <>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
                        <div>
                          <h3 className="text-lg sm:text-xl font-semibold mb-2">
                            {formatDate(reservation.reservationDate)}
                          </h3>
                          <p className="text-sm sm:text-base text-gray-600">
                            {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                          </p>
                          <p className="text-sm sm:text-base text-gray-600">
                            {reservation.partySize} {reservation.partySize === 1 ? 'person' : 'people'}
                          </p>
                          <p className="text-sm sm:text-base text-gray-600">
                            <strong>Name:</strong> {reservation.name}
                          </p>
                          <p className="text-sm sm:text-base text-gray-600">
                            <strong>Phone:</strong> {reservation.phoneNumber}
                          </p>
                          <p className="text-sm sm:text-base text-gray-600">
                            <strong>Seat Type:</strong> {reservation.seatType === 'counter' ? 'Counter' : 'Table'}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleEdit(reservation)}
                            size="sm"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleCancel(reservation.id)}
                            size="sm"
                            variant="danger"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Privacy Policy Link */}
          <div className="max-w-2xl mx-auto mt-8 text-center">
            <p className="text-xs text-gray-600">
              We only use your email address to send a reminder on the morning of your reservation.
              See our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> for details.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

export default MyReservationsPage; 